import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import permitsService, { PermitCondition, PermitRow } from '../../services/api/permitsService';

interface PermitLookAheadPanelProps {
  projectId?: string;
  startDate: string;
  endDate: string;
}

const closedStatuses = new Set(['closed', 'rejected']);

const daysUntil = (dateValue: string | null): number | null => {
  if (!dateValue) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
};

const within = (dateValue: string | null, startDate: string, endDate: string): boolean => {
  if (!dateValue) return false;
  return dateValue >= startDate && dateValue <= endDate;
};

export default function PermitLookAheadPanel({ projectId, startDate, endDate }: PermitLookAheadPanelProps) {
  const [permits, setPermits] = useState<PermitRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    permitsService.list({
      projectId,
      expiresAfter: startDate,
      expiresBefore: endDate,
    })
      .then((rows) => {
        if (!cancelled) setPermits(rows.filter((permit) => !closedStatuses.has(permit.status)));
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load permit look-ahead');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, startDate, endDate]);

  const dueConditions = useMemo(() => permits.flatMap((permit) => (
    (permit.conditions ?? [])
      .filter((condition) => ['open', 'in_progress'].includes(condition.status) && within(condition.dueDate, startDate, endDate))
      .map((condition) => ({ permit, condition }))
  )), [permits, startDate, endDate]);

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6">Permit Look-Ahead</Typography>
            <Typography variant="body2" color="text.secondary">
              Expiring permits and due conditions in the selected window.
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {!error && !loading && permits.length === 0 && dueConditions.length === 0 && (
            <Alert severity="success">No permits or conditions due in this window.</Alert>
          )}

          {permits.length > 0 && (
            <List dense disablePadding>
              {permits.slice(0, 5).map((permit) => (
                <ListItem key={permit.id} disableGutters secondaryAction={<Chip size="small" label={`${daysUntil(permit.expiryDate) ?? '-'}d`} />}>
                  <ListItemText
                    primary={permit.title}
                    secondary={`${permit.permitNumber} · ${permit.status.replace('_', ' ')} · expires ${permit.expiryDate ?? 'not set'}`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {dueConditions.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">Due Conditions</Typography>
              <List dense disablePadding>
                {dueConditions.slice(0, 5).map(({ permit, condition }: { permit: PermitRow; condition: PermitCondition }) => (
                  <ListItem key={condition.id} disableGutters>
                    <ListItemText
                      primary={condition.description}
                      secondary={`${permit.permitNumber} · ${condition.status.replace('_', ' ')} · due ${condition.dueDate ?? 'not set'}`}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
